/**
 * createStore.ts — Generic store factory for Pioneer v2 State Sync Framework.
 *
 * Provides:
 * - Cache hydration from storage on creation
 * - Write-through persistence on mutation
 * - Cross-tab sync via BroadcastChannel
 * - subscribe() compatible with React 18's useSyncExternalStore
 *
 * Persistence modes:
 * - 'session'    — sessionStorage (dies on tab close). Default.
 * - 'persistent' — IndexedDB via idb-keyval (survives browser restart).
 *                   Requires preloadIdb() to be called at app boot BEFORE
 *                   React renders, so hydration is synchronous.
 */

import idbStorage from "../storage/idbStorageAdapter";
import sessionStore from "../storage/sessionStorageAdapter";

import { createSyncChannel, SyncChannel } from "./broadcastAdapter";

export interface StoreConfig<T> {
  /** Storage key for persistence */
  cacheKey: string;
  /** Initial value when no cache exists */
  initialValue: T;
  /** Time-to-live for cache freshness (ms). Default: 5 * 60 * 1000 */
  ttlMs?: number;
  /** BroadcastChannel name for cross-tab sync. Omit to disable. */
  channelName?: string;
  /**
   * Persistence backend.
   * - 'session'    — sessionStorage (dies on tab close). Default.
   * - 'persistent' — IndexedDB (survives browser restart).
   *
   * When 'persistent', call `preloadIdb()` at app boot before React renders.
   */
  persistence?: 'session' | 'persistent';
}

export interface Store<T> {
  /** Current snapshot (for useSyncExternalStore) */
  getSnapshot(): T;
  /** Subscribe to changes (for useSyncExternalStore) */
  subscribe(listener: () => void): () => void;
  /** Replace entire state (direct value or updater function) */
  setState(nextOrUpdater: T | ((prev: T) => T)): void;
  /** Patch state (shallow merge) */
  patchState(partial: Partial<T>): void;
  /** Check if cache is stale */
  isStale(): boolean;
  /** Get timestamp of last update */
  getLastUpdated(): number;
  /** Clear state and cache */
  clear(): void;
  /** Destroy channel (cleanup) */
  destroy(): void;
}

const DEFAULT_TTL_MS = 5 * 60 * 1000;

/**
 * Attempt to parse a JSON string. Returns null on failure.
 */
function tryParseJson<T>(raw: string | null): T | null {
  if (raw === null) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

interface CacheEnvelope<T> {
  data: T;
  lastUpdated: number;
}

export function createStore<T extends object>(
  config: StoreConfig<T>
): Store<T> {
  const {
    cacheKey,
    initialValue,
    ttlMs = DEFAULT_TTL_MS,
    channelName,
    persistence = 'session',
  } = config;

  // ── Pick storage adapter ─────────────────────────────────────────────────
  const storage = persistence === 'persistent' ? idbStorage : sessionStore;

  // ── Hydrate from storage ─────────────────────────────────────────────────
  let state: T = initialValue;
  let lastUpdated = 0;

  const raw = storage.getItem(cacheKey);
  if (raw !== null) {
    const envelope = tryParseJson<CacheEnvelope<T>>(raw);
    if (envelope !== null && typeof envelope === "object" && "data" in envelope) {
      state = envelope.data;
      lastUpdated = typeof envelope.lastUpdated === "number" ? envelope.lastUpdated : 0;
    } else {
      // Legacy format: raw data without envelope
      const legacy = tryParseJson<T>(raw);
      if (legacy !== null) {
        state = legacy;
        lastUpdated = Date.now();
      }
    }

    // `loading` and `error` are transient UI state that should never survive
    // persistence.  If the cache was written while a fetch was in flight,
    // `loading: true` could be persisted and cause blank columns on the next
    // boot.  Similarly, a persisted `error` string (e.g. "Failed to fetch")
    // would prevent the UI from attempting a fresh load on the next session.
    // Force both to their safe defaults after hydration — the action that
    // loads data will set them again if needed.
    if ("loading" in state && (state as Record<string, unknown>).loading === true) {
      state = { ...state, loading: false };
    }
    if ("error" in state && (state as Record<string, unknown>).error != null) {
      state = { ...state, error: null };
    }
  }

  // ── Listeners ─────────────────────────────────────────────────────────────
  const listeners = new Set<() => void>();

  function notify(): void {
    listeners.forEach((fn) => fn());
  }

  // ── Cross-tab channel ─────────────────────────────────────────────────────
  const channel: SyncChannel = channelName
    ? createSyncChannel(channelName)
    : {
        postMessage: () => undefined,
        onMessage: () => () => undefined,
        close: () => undefined,
      };

  // Listen for cross-tab updates — update state but do NOT re-broadcast
  channel.onMessage((data: unknown) => {
    if (
      data !== null &&
      typeof data === "object" &&
      "type" in data &&
      "payload" in data &&
      "lastUpdated" in data &&
      (data.type === "setState" || data.type === "clear") &&
      typeof data.lastUpdated === "number"
    ) {
      const incomingTimestamp = data.lastUpdated;
      
      // BUG FIX (task 01147): Ignore stale cross-tab messages.
      // When a user has multiple tabs open and rapidly clicks controls that
      // trigger state updates (e.g. AND/OR/Individual hashtag filter toggle),
      // BroadcastChannel may deliver messages out of order. If we blindly
      // apply every message, older state can overwrite newer local changes.
      //
      // SOLUTION: Only apply the incoming state if its timestamp is NEWER
      // than our current lastUpdated. This ensures local changes always take
      // precedence over stale cross-tab sync messages.
      //
      // EDGE CASE: If two tabs update within the same millisecond, the last
      // one wins (standard eventual consistency). This is acceptable because
      // users rarely interact with multiple tabs simultaneously.
      if (incomingTimestamp <= lastUpdated) {
        // Stale message — ignore it. Our local state is newer.
        return;
      }
      
      const prevState = state;
      state = data.payload as T;
      
      // Debug logging for workspace switch investigation (task 01147)
      if (cacheKey === 'pioneer_org_team_store' && 'selectedOrgId' in prevState && 'selectedOrgId' in state) {
        const prev = prevState as { selectedOrgId: string };
        const next = state as { selectedOrgId: string };
        if (prev.selectedOrgId !== next.selectedOrgId) {
          // eslint-disable-next-line no-console
          console.log('[01147-DEBUG] orgTeamStore received BroadcastChannel message - selectedOrgId changed:', {
            from: prev.selectedOrgId,
            to: next.selectedOrgId,
            channelName,
            timestamp: Date.now(),
            incomingTimestamp,
            ourPreviousTimestamp: lastUpdated
          });
        }
      }
      
      lastUpdated = incomingTimestamp;
      notify();
    }
  });

  // ── Persist helper ────────────────────────────────────────────────────────
  // Debounce persistence so rapid-fire mutations (e.g. mergeItems + 600×
  // setCachedItem + bridge patches) only serialize once after the burst
  // settles.  In-memory state and React notifications are still synchronous
  // — only the expensive JSON.stringify + storage write + BroadcastChannel
  // post is deferred.
  let persistTimer: ReturnType<typeof setTimeout> | null = null;
  const PERSIST_DEBOUNCE_MS = 50; // 50ms — fast enough to feel instant

  function schedulePersist(): void {
    if (persistTimer !== null) clearTimeout(persistTimer);
    persistTimer = setTimeout(() => {
      persistTimer = null;
      const envelope: CacheEnvelope<T> = { data: state, lastUpdated };
      storage.setItem(cacheKey, JSON.stringify(envelope));
      channel.postMessage({ type: "setState", payload: state, lastUpdated });
    }, PERSIST_DEBOUNCE_MS);
  }

  /** Persist immediately (bypass debounce).  Used by clear(). */
  function persistNow(next: T, ts: number): void {
    if (persistTimer !== null) {
      clearTimeout(persistTimer);
      persistTimer = null;
    }
    const envelope: CacheEnvelope<T> = { data: next, lastUpdated: ts };
    storage.setItem(cacheKey, JSON.stringify(envelope));
  }

  // ── Store API ─────────────────────────────────────────────────────────────
  return {
    getSnapshot(): T {
      return state;
    },

    subscribe(listener: () => void): () => void {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },

    setState(nextOrUpdater: T | ((prev: T) => T)): void {
      const prevState = state;
      state = typeof nextOrUpdater === "function"
        ? (nextOrUpdater as (prev: T) => T)(state)
        : nextOrUpdater;
      
      // Debug logging for workspace switch investigation (task 01147)
      if (cacheKey === 'pioneer_org_team_store' && 'selectedOrgId' in prevState && 'selectedOrgId' in state) {
        const prev = prevState as { selectedOrgId: string };
        const next = state as { selectedOrgId: string };
        if (prev.selectedOrgId !== next.selectedOrgId) {
          // eslint-disable-next-line no-console
          console.log('[01147-DEBUG] orgTeamStore.setState - selectedOrgId changed:', {
            from: prev.selectedOrgId,
            to: next.selectedOrgId,
            timestamp: Date.now(),
            stack: new Error().stack?.split('\n').slice(2, 6).join('\n')
          });
        }
      }
      
      lastUpdated = Date.now();
      notify();
      schedulePersist();
    },

    patchState(partial: Partial<T>): void {
      const prevState = state;
      state = { ...state, ...partial };
      
      // Debug logging for workspace switch investigation (task 01147)
      if (cacheKey === 'pioneer_org_team_store' && 'selectedOrgId' in partial) {
        const prev = prevState as { selectedOrgId: string };
        const partialTyped = partial as { selectedOrgId?: string };
        // eslint-disable-next-line no-console
        console.log('[01147-DEBUG] orgTeamStore.patchState - selectedOrgId patched:', {
          from: prev.selectedOrgId,
          to: partialTyped.selectedOrgId,
          timestamp: Date.now(),
          stack: new Error().stack?.split('\n').slice(2, 6).join('\n')
        });
      }
      
      lastUpdated = Date.now();
      notify();
      schedulePersist();
    },

    isStale(): boolean {
      if (lastUpdated === 0) return true;
      return Date.now() - lastUpdated > ttlMs;
    },

    getLastUpdated(): number {
      return lastUpdated;
    },

    clear(): void {
      state = initialValue;
      lastUpdated = 0;
      // Cancel any pending debounced persist, then clear storage immediately.
      persistNow(initialValue, 0);
      storage.removeItem(cacheKey);
      notify();
      channel.postMessage({ type: "clear", payload: initialValue, lastUpdated: 0 });
    },

    destroy(): void {
      if (persistTimer !== null) clearTimeout(persistTimer);
      channel.close();
      listeners.clear();
    },
  };
}
